﻿(() => {
  if (window.PrintMyPadEtsyReviews) {
    return;
  }

  const feedCache = new Map();
  const DISPLAY_RATING = 5;
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function toText(value) {
    if (value === null || value === undefined) {
      return "";
    }

    return String(value).trim();
  }

  function parseJsonSafe(value) {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  function clampRating(value) {
    let nextValue = Number(value);

    if (!Number.isFinite(nextValue)) {
      return 0;
    }

    if (nextValue > 5 && nextValue <= 10) {
      nextValue = nextValue / 2;
    } else if (nextValue > 5 && nextValue <= 100) {
      nextValue = nextValue / 20;
    }

    return Math.max(0, Math.min(5, Math.round(nextValue * 10) / 10));
  }

  function parseRating(value) {
    if (typeof value === "number") {
      return clampRating(value);
    }

    const text = toText(value);
    if (!text) {
      return 0;
    }

    const match = text.match(/(\d+(\.\d+)?)/);
    if (!match) {
      return 0;
    }

    return clampRating(parseFloat(match[1]));
  }

  function isAllowedFeedUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      const host = parsed.hostname.toLowerCase();
      const protocol = parsed.protocol.toLowerCase();

      if (parsed.origin === window.location.origin) {
        return ["https:", "http:"].includes(protocol);
      }

      if (protocol !== "https:") {
        return false;
      }

      return (
        host === "cdn.shopify.com" ||
        host.endsWith(".shopify.com") ||
        host.endsWith(".myshopify.com")
      );
    } catch (_error) {
      return false;
    }
  }

  function parseUrlList(raw) {
    return toText(raw)
      .split(/[\r\n,]+/)
      .map((entry) => entry.trim())
      .filter((entry) => entry && isAllowedFeedUrl(entry));
  }

  function parseDateParts(value) {
    const text = toText(value);
    if (!text) {
      return { label: "", timestamp: 0, raw: "" };
    }

    // Server feed dates are ISO calendar dates; read them in local time like the Etsy format.
    const isoDateMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const usDateMatch = isoDateMatch
      ? [text, isoDateMatch[2], isoDateMatch[3], isoDateMatch[1]]
      : text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usDateMatch) {
      const month = parseInt(usDateMatch[1], 10) - 1;
      const day = parseInt(usDateMatch[2], 10);
      const year = parseInt(usDateMatch[3], 10);
      const parsed = new Date(year, month, day);

      if (!Number.isNaN(parsed.getTime())) {
        return {
          label: parsed.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
          timestamp: parsed.getTime(),
          raw: text,
        };
      }
    }

    const fallback = new Date(text);
    if (Number.isNaN(fallback.getTime())) {
      return { label: "", timestamp: 0, raw: text };
    }

    return {
      label: fallback.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      timestamp: fallback.getTime(),
      raw: text,
    };
  }

  const REVIEW_HINT_KEYS = [
    "rating",
    "stars",
    "score",
    "review",
    "review_text",
    "message",
    "comment",
    "content",
    "body",
    "text",
    "buyer_name",
    "author",
    "name",
    "reviewer",
    "shop_name",
    "store_name",
    "created_at",
    "date",
    "date_reviewed",
    "listing_title",
    "product_title",
  ];

  function isLikelyReviewObject(entry) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      return false;
    }

    const keys = Object.keys(entry).map((key) => key.toLowerCase());
    return keys.some((key) => REVIEW_HINT_KEYS.includes(key));
  }

  function findReviewArrays(node, depth) {
    if (depth > 5 || node === null || node === undefined) {
      return [];
    }

    if (Array.isArray(node)) {
      if (!node.length) {
        return [];
      }

      const objectItems = node.filter(
        (item) => item && typeof item === "object" && !Array.isArray(item)
      );

      if (
        objectItems.length > 0 &&
        objectItems.some((item) => isLikelyReviewObject(item))
      ) {
        return [node];
      }

      return node.flatMap((item) => findReviewArrays(item, depth + 1));
    }

    if (typeof node === "object") {
      return Object.values(node).flatMap((value) =>
        findReviewArrays(value, depth + 1)
      );
    }

    return [];
  }

  function lowerCaseMap(entry) {
    const map = {};
    Object.keys(entry).forEach((key) => {
      map[key.toLowerCase()] = entry[key];
    });
    return map;
  }

  function pullValue(entry, keys) {
    const map = lowerCaseMap(entry);

    for (const key of keys) {
      if (entry[key] !== undefined && entry[key] !== null) {
        return entry[key];
      }

      const lowerKey = key.toLowerCase();
      if (map[lowerKey] !== undefined && map[lowerKey] !== null) {
        return map[lowerKey];
      }
    }

    return "";
  }

  function sourceNameFromUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      const filename = parsed.pathname.split("/").pop() || "";
      return (
        filename.replace(/\.[^/.]+$/, "").replace(/[-_]+/g, " ").trim() ||
        "Etsy"
      );
    } catch (_error) {
      return "Etsy";
    }
  }

  function normalizeReview(entry, fallbackStore) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      return null;
    }

    const rating = parseRating(
      pullValue(entry, [
        "rating",
        "stars",
        "score",
        "star_rating",
        "review_rating",
        "overall_rating",
      ])
    );

    if (rating <= 0) {
      return null;
    }

    const title = toText(pullValue(entry, ["title", "headline", "subject"]));
    const body = toText(
      pullValue(entry, [
        "message",
        "review",
        "review_text",
        "comment",
        "body",
        "content",
        "description",
      ])
    );
    const author = toText(
      pullValue(entry, ["buyer_name", "author", "name", "user_name", "reviewer"])
    );
    const listing = toText(
      pullValue(entry, [
        "listing_title",
        "product_title",
        "product_name",
        "item_title",
      ])
    );
    const store =
      toText(
        pullValue(entry, ["shop_name", "store_name", "shop", "store"])
      ) || fallbackStore;
    const parsedDate = parseDateParts(
      pullValue(entry, ["date_reviewed", "created_at", "date", "review_date"])
    );

    return {
      rating,
      title,
      body,
      author,
      listing,
      store,
      dateLabel: parsedDate.label,
      dateRaw: parsedDate.raw,
      timestamp: parsedDate.timestamp,
    };
  }

  function dedupeReviews(reviews) {
    const seen = new Set();

    return reviews.filter((review) => {
      const key = [
        toText(review.author).toLowerCase(),
        toText(review.title).toLowerCase(),
        toText(review.body).toLowerCase(),
        String(review.rating),
        toText(review.dateRaw).toLowerCase(),
        toText(review.store).toLowerCase(),
      ].join("|");

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }

  function parsePayloadIntoReviews(payload, fallbackStore) {
    const arrays = findReviewArrays(payload, 0);
    const entries =
      arrays.length > 0
        ? arrays.flat()
        : isLikelyReviewObject(payload)
          ? [payload]
          : [];

    return entries
      .map((entry) => normalizeReview(entry, fallbackStore))
      .filter(Boolean);
  }

  async function fetchReviewsFromUrl(url) {
    const response = await fetch(url, { method: "GET", credentials: "omit" });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    const text = await response.text();
    const payload = parseJsonSafe(text);
    if (!payload) {
      throw new Error("Invalid JSON response");
    }

    return parsePayloadIntoReviews(payload, sourceNameFromUrl(url));
  }

  function parseInlineReviews(rawInlineJson) {
    const stringValue = parseJsonSafe(rawInlineJson);
    const payload = parseJsonSafe(toText(stringValue));
    if (!payload) {
      return [];
    }

    return parsePayloadIntoReviews(payload, "Etsy");
  }

  function compareReviews(a, b) {
    if (a.timestamp !== b.timestamp) {
      return b.timestamp - a.timestamp;
    }

    if (a.rating !== b.rating) {
      return b.rating - a.rating;
    }

    return toText(a.author).localeCompare(toText(b.author));
  }

  function shuffleReviews(reviews) {
    const shuffled = reviews.slice();

    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[randomIndex]] = [
        shuffled[randomIndex],
        shuffled[index],
      ];
    }

    return shuffled;
  }

  function summarizeReviews(reviews) {
    if (!reviews.length) {
      return {
        average: 0,
        count: 0,
      };
    }

    const total = reviews.reduce((sum, review) => sum + review.rating, 0);
    return {
      average: Math.round((total / reviews.length) * 10) / 10,
      count: reviews.length,
    };
  }

  async function loadFeed(config) {
    const inlineReviews = parseInlineReviews(config.inlineJson);
    const urls = parseUrlList(config.urlsRaw);
    const groups = await Promise.all(
      urls.map(async (url) => {
        try {
          return await fetchReviewsFromUrl(url);
        } catch (_error) {
          return [];
        }
      })
    );

    const allReviews = dedupeReviews([...inlineReviews, ...groups.flat()]).sort(
      compareReviews
    );

    return {
      aggregate: summarizeReviews(allReviews),
      allReviews,
      displayReviews: allReviews.filter(
        (review) => review.rating === DISPLAY_RATING && toText(review.body)
      ),
    };
  }

  // Feed rendered by Liquid from the pmp.etsy_reviews metafield: the summary covers every review,
  // while `reviews` holds only the quotes, so the aggregate comes from the feed itself.
  function loadServerFeed(rawJson, minStars) {
    const payload = parseJsonSafe(rawJson);
    if (!payload || !Array.isArray(payload.reviews)) {
      return null;
    }

    const allReviews = parsePayloadIntoReviews(
      payload.reviews,
      toText(payload.source) || "Etsy"
    );
    const threshold = parseRating(minStars) || 4;

    return {
      source: toText(payload.source),
      aggregate: {
        average: clampRating(payload.average),
        count: parseInt(payload.count, 10) || allReviews.length,
      },
      allReviews,
      displayReviews: allReviews.filter(
        (review) => review.rating >= threshold && toText(review.body)
      ),
    };
  }

  function getFeed(root) {
    const serverJson = root.querySelector("[data-etsy-server-feed]")?.textContent;
    if (serverJson) {
      const serverFeed = loadServerFeed(serverJson, root.dataset.minStars);
      if (serverFeed) {
        return Promise.resolve(serverFeed);
      }
    }

    const inlineJson =
      root.querySelector("[data-etsy-inline-json]")?.textContent || "\"\"";
    const urlsRaw = root.dataset.reviewJsonUrls || "";
    const cacheKey = JSON.stringify([urlsRaw, inlineJson]);

    if (!feedCache.has(cacheKey)) {
      feedCache.set(cacheKey, loadFeed({ inlineJson, urlsRaw }));
    }

    return feedCache.get(cacheKey);
  }

  function truncateText(text, limit) {
    const normalized = toText(text);
    const nextLimit = parseInt(limit, 10);

    if (!normalized || !Number.isFinite(nextLimit) || nextLimit < 20) {
      return normalized;
    }

    if (normalized.length <= nextLimit) {
      return normalized;
    }

    return `${normalized.slice(0, nextLimit).trimEnd()}...`;
  }

  function formatRating(value) {
    const rating = Number(value);

    if (!Number.isFinite(rating)) {
      return "";
    }

    return Number.isInteger(rating) ? String(rating) : rating.toFixed(1);
  }

  function setText(target, value) {
    if (target) {
      target.textContent = value;
    }
  }

  function setStatus(root, message, isVisible) {
    const status = root.querySelector("[data-review-status]");
    if (!status) {
      return;
    }

    status.textContent = message;
    status.hidden = !isVisible;
  }

  function populateSummary(summaryEl, aggregate, sourceLabel) {
    if (!summaryEl || aggregate.count <= 0) {
      if (summaryEl) {
        summaryEl.hidden = true;
      }
      return;
    }

    const stars = summaryEl.querySelector(".rating-product__stars");
    if (stars) {
      stars.style.setProperty("--rating", aggregate.average.toFixed(1));
      stars.setAttribute(
        "aria-label",
        `Rated ${aggregate.average.toFixed(1)} out of 5 stars`
      );
    }

    setText(
      summaryEl.querySelector("[data-review-rating]"),
      aggregate.average.toFixed(1)
    );
    setText(
      summaryEl.querySelector("[data-review-count-label]"),
      aggregate.count === 1 ? "1 review" : `${aggregate.count} reviews`
    );
    setText(
      summaryEl.querySelector("[data-review-source-label]"),
      toText(sourceLabel) || "Etsy shop reviews"
    );

    summaryEl.hidden = false;
  }

  function createReviewCard(review, options = {}) {
    const article = document.createElement("article");
    article.className = "etsy-review-card";

    const header = document.createElement("div");
    header.className = "etsy-review-card__header";

    const stars = document.createElement("span");
    stars.className = "rating-product__stars etsy-review-card__stars";
    stars.setAttribute("aria-hidden", "true");
    stars.style.setProperty("--rating", review.rating.toFixed(1));
    header.appendChild(stars);

    const rating = document.createElement("span");
    rating.className = "etsy-review-card__rating";
    rating.textContent = `${formatRating(review.rating)}/5`;
    header.appendChild(rating);

    if (options.showDate && review.dateLabel) {
      const date = document.createElement("span");
      date.className = "etsy-review-card__date";
      date.textContent = review.dateLabel;
      header.appendChild(date);
    }

    article.appendChild(header);

    if (review.title) {
      const title = document.createElement("h3");
      title.className = "etsy-review-card__title";
      title.textContent = review.title;
      article.appendChild(title);
    }

    const body = document.createElement("p");
    body.className = "etsy-review-card__body";
    body.textContent = truncateText(review.body, options.charLimit);
    article.appendChild(body);

    const footer = document.createElement("div");
    footer.className = "etsy-review-card__footer";

    if (options.showAuthor && review.author) {
      const author = document.createElement("span");
      author.className = "etsy-review-card__pill";
      author.textContent = review.author;
      footer.appendChild(author);
    }

    if (review.store && options.showStore) {
      const store = document.createElement("span");
      store.className = "etsy-review-card__pill";
      store.textContent = review.store;
      footer.appendChild(store);
    }

    if (footer.childNodes.length) {
      article.appendChild(footer);
    }

    return article;
  }

  function buildPagination(instanceId) {
    const pagination = document.createElement("div");
    pagination.className = "etsy-review-swiper__pagination";
    pagination.id = `etsy-review-pagination-${instanceId}`;
    return pagination;
  }

  function initializeSwiper(swiperEl) {
    if (typeof swiperEl.initialize === "function") {
      swiperEl.initialize();
      return;
    }

    customElements.whenDefined("swiper-container").then(() => {
      if (typeof swiperEl.initialize === "function") {
        swiperEl.initialize();
      }
    });
  }

  function renderSlider(root, container, reviews, isSingle) {
    const instanceId = root.dataset.instanceId || String(Date.now());
    const wrapper = document.createElement("div");
    wrapper.className = "etsy-review-slider";

    const swiper = document.createElement("swiper-container");
    swiper.setAttribute("init", "false");
    swiper.setAttribute("watch-overflow", "true");
    swiper.setAttribute("speed", "450");
    swiper.setAttribute("pagination", "true");
    swiper.setAttribute(
      "pagination-el",
      `#etsy-review-pagination-${instanceId}`
    );
    swiper.setAttribute("pagination-clickable", "true");
    swiper.className = "etsy-review-swiper";

    if (isSingle) {
      swiper.setAttribute("effect", "fade");
      swiper.setAttribute("fade-effect-cross-fade", "true");
      swiper.setAttribute("slides-per-view", "1");
      if (!prefersReducedMotion && root.dataset.autoplay === "true" && reviews.length > 1) {
        swiper.setAttribute("autoplay-delay", root.dataset.autoplayDelay || "4500");
        swiper.setAttribute("loop", "true");
      }
    } else {
      swiper.setAttribute("slides-per-view", "1");
      swiper.setAttribute("space-between", "16");
      swiper.setAttribute(
        "breakpoints",
        "{\"768\":{\"slidesPerView\":2,\"spaceBetween\":16},\"1024\":{\"slidesPerView\":3,\"slidesPerGroup\":3,\"spaceBetween\":24}}"
      );
      if (!prefersReducedMotion && root.dataset.autoplay === "true" && reviews.length > 3) {
        swiper.setAttribute("autoplay-delay", root.dataset.autoplayDelay || "5000");
        swiper.setAttribute("loop", "true");
      }
    }

    reviews.forEach((review) => {
      const slide = document.createElement("swiper-slide");
      slide.className = "etsy-review-slide";
      slide.appendChild(
        createReviewCard(review, {
          showAuthor: root.dataset.showAuthor !== "false",
          showDate: root.dataset.showDate !== "false",
          showStore: false,
          charLimit: root.dataset.charLimit,
        })
      );
      swiper.appendChild(slide);
    });

    wrapper.appendChild(swiper);
    wrapper.appendChild(buildPagination(instanceId));
    container.appendChild(wrapper);
    initializeSwiper(swiper);
  }

  function createMarqueeItem(review, charLimit) {
    const item = document.createElement("div");
    item.className = "etsy-review-marquee__item";

    const text = document.createElement("span");
    text.className = "etsy-review-marquee__text";
    text.textContent = `\"${truncateText(review.body, charLimit)}\"`;
    item.appendChild(text);

    const meta = document.createElement("span");
    meta.className = "etsy-review-marquee__meta";
    meta.textContent = review.author
      ? `${review.author} • ${formatRating(review.rating)}`
      : `${formatRating(review.rating)}/5`;
    item.appendChild(meta);

    return item;
  }

  function renderMarquee(root, container, reviews) {
    const marquee = document.createElement("div");
    marquee.className = "etsy-review-marquee";

    const track = document.createElement("div");
    track.className = "etsy-review-marquee__track";

    const safeReviews = reviews.slice(0, Math.min(reviews.length, 8));
    for (let repeat = 0; repeat < 2; repeat += 1) {
      safeReviews.forEach((review) => {
        const item = createMarqueeItem(review, root.dataset.charLimit || "120");
        if (repeat > 0) {
          item.setAttribute("aria-hidden", "true");
        }
        track.appendChild(item);
      });
    }

    const duration =
      parseInt(root.dataset.marqueeSpeed, 10) ||
      Math.max(28, safeReviews.length * 6);
    track.style.setProperty("--marquee-duration", `${duration}s`);

    marquee.appendChild(track);
    container.appendChild(marquee);
  }

  function renderShowcase(root, feed) {
    const container = root.querySelector("[data-review-body]");
    const sourceLabel = root.dataset.sourceLabel || "Etsy shop reviews";
    const maxReviews = parseInt(root.dataset.maxReviews, 10) || 6;

    if (!container) {
      return;
    }

    container.innerHTML = "";

    if (root.dataset.showSummary === "true") {
      populateSummary(root.querySelector("[data-review-summary]"), feed.aggregate, feed.source || sourceLabel);
    }

    if (!feed.aggregate.count) {
      setStatus(root, "Customer reviews are not available right now.", true);
      return;
    }

    const selectedReviews = shuffleReviews(feed.displayReviews).slice(0, maxReviews);

    if (!selectedReviews.length) {
      setStatus(root, "Customer reviews are not available right now.", true);
      return;
    }

    setStatus(root, "", false);
    switch (root.dataset.layoutMode) {
      case "single_quote_slider":
        renderSlider(root, container, selectedReviews, true);
        break;
      case "trust_marquee":
        renderMarquee(root, container, selectedReviews);
        break;
      default:
        renderSlider(root, container, selectedReviews, false);
        break;
    }
  }

  function updateProductQuote(root, review) {
    const quoteWrapper = root.querySelector("[data-product-quote]");
    if (!quoteWrapper || !review) {
      if (quoteWrapper) {
        quoteWrapper.hidden = true;
      }
      return;
    }

    const quoteText = quoteWrapper.querySelector("[data-product-quote-text]");
    const quoteAuthor = quoteWrapper.querySelector("[data-product-quote-author]");
    const quoteDate = quoteWrapper.querySelector("[data-product-quote-date]");
    const quoteStars = quoteWrapper.querySelector("[data-product-quote-stars]");

    setText(quoteText, truncateText(review.body, root.dataset.charLimit || "180"));
    setText(quoteAuthor, review.author || "Verified Etsy buyer");
    setText(quoteDate, review.dateLabel || "");

    if (quoteStars) {
      quoteStars.style.setProperty("--rating", review.rating.toFixed(1));
    }

    quoteWrapper.hidden = false;
    quoteWrapper.classList.remove("is-animating");
    window.requestAnimationFrame(() => {
      quoteWrapper.classList.add("is-animating");
    });
  }

  function renderProductWidget(root, feed) {
    const sourceLabel = root.dataset.sourceLabel || "Etsy shop reviews";
    const maxQuotes = parseInt(root.dataset.maxQuotes, 10) || 5;
    const showSummary = root.dataset.showSummary !== "false";
    const showQuote = root.dataset.showQuote === "true";
    const summary = root.querySelector("[data-review-summary]");

    if (showSummary) {
      populateSummary(summary, feed.aggregate, feed.source || sourceLabel);
    } else if (summary) {
      summary.hidden = true;
    }

    if (!feed.aggregate.count) {
      setStatus(root, "Customer reviews are not available right now.", true);
      return;
    }

    const quotePool = shuffleReviews(feed.displayReviews).slice(0, maxQuotes);

    if (!showQuote || !quotePool.length) {
      setStatus(root, "", false);
      updateProductQuote(root, null);
      return;
    }

    if (root._etsyReviewInterval) {
      window.clearInterval(root._etsyReviewInterval);
      root._etsyReviewInterval = null;
    }

    let quoteIndex = 0;
    updateProductQuote(root, quotePool[quoteIndex]);
    setStatus(root, "", false);

    if (!prefersReducedMotion && root.dataset.autoplay === "true" && quotePool.length > 1) {
      const delay = parseInt(root.dataset.autoplayDelay, 10) || 5000;
      root._etsyReviewInterval = window.setInterval(() => {
        quoteIndex = (quoteIndex + 1) % quotePool.length;
        updateProductQuote(root, quotePool[quoteIndex]);
      }, delay);
    }
  }

  async function initWidget(root) {
    if (!root || root.dataset.etsyReviewWidgetReady === "true") {
      return;
    }

    root.dataset.etsyReviewWidgetReady = "true";
    if (!root.querySelector("[data-etsy-server-feed]")) {
      setStatus(root, "Loading reviews...", true);
    }

    try {
      const feed = await getFeed(root);

      if (root.dataset.etsyReviewWidget === "product") {
        renderProductWidget(root, feed);
      } else {
        renderShowcase(root, feed);
      }
    } catch (_error) {
      setStatus(root, "Customer reviews are temporarily unavailable.", true);
    }
  }

  function initWidgets(scope) {
    const container = scope || document;
    container
      .querySelectorAll("[data-etsy-review-widget]")
      .forEach((root) => initWidget(root));
  }

  window.PrintMyPadEtsyReviews = {
    init: initWidgets,
    loadFeed,
  };
  window.dispatchEvent(new Event('pmp:reviews-ready'));

})();
